import { EventCard } from "./EventCard";
import { Category } from "./EventPage";

// EventGrid.ts
export type Event = {
  name: string;
  description: string;
  date?: string;
  category?: string;
  price?: string;
  venue?: string;
  imageUrl?: string;
  detailedDescription?: string;
  rules?: string[];
  teamSize?: string;
  duration?: string;
  prizes?: {
    first?: string;
    second?: string;
    third?: string;
    others?: string[];
  };
  contact?: {
    email?: string;
    phone?: string;
    coordinator?: string;
  };
  requirements?: string[];
  registrationLink?: string;
};

interface EventGridProps {
  events: Event[];
}

const styles = `
  @keyframes empty-pulse {
    0%, 100% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.01); }
  }
`;

// Helper function to convert string to valid Category type
const getValidCategory = (category?: string): Category => {
  const validCategories: Category[] = ["flagship", "technical", "non-technical", "games"];
  
  // "all" is a special category for filtering, not for individual events
  // So we don't include it as a valid category for events
  
  if (category && validCategories.includes(category as Category)) {
    return category as Category;
  }
  
  // Default to "flagship" if category is invalid or undefined
  return "flagship";
};

export const EventGrid = ({ events }: EventGridProps) => {
  if (events.length === 0) {
    return (
      <>
        <style>{styles}</style>
        <div 
          style={{
            textAlign: 'center',
            padding: 'clamp(40px, 8vw, 60px) clamp(16px, 4vw, 20px)',
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'clamp(16px, 3vw, 24px)',
            border: '1px solid var(--color-border)',
            animation: 'empty-pulse 3s ease-in-out infinite',
          }}
        >
          <div 
            style={{
              fontSize: 'clamp(2rem, 6vw, 3rem)',
              marginBottom: '16px',
              opacity: 0.6,
              color: 'var(--color-text-muted)',
            }}
          >
            🎭
          </div>
          <p 
            style={{
              color: 'var(--color-text-secondary)',
              fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
              fontWeight: 500,
              marginBottom: '8px',
            }}
          >
            No events found
          </p>
          <p 
            style={{
              color: 'var(--color-text-muted)',
              fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
            }}
          >
            Try different filters or search terms
          </p>
        </div>
      </>
    );
  }

  return (
    <div 
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
        gap: 'clamp(16px, 3vw, 24px)',
      }}
    >
      {events.map((event, index) => (
        <EventCard 
          key={event.name} 
          event={event} 
          index={index} 
          category={getValidCategory(event.category)} // Use the helper function
        />
      ))}
    </div>
  );
};